import { Equal, Match } from 'effect';
import { DataSourceFilterEnum } from '../types';
import { useRecoilValue } from 'recoil';
import { allDataSourcesSelector } from 'venn-state';
import React, { useEffect, useMemo } from 'react';

interface UseDataSourceFilterStateProps {
  selected: DataSourceFilterEnum[];
}
export const useDataSourceFilterState = ({ selected }: UseDataSourceFilterStateProps) => {
  const [open, setOpen] = React.useState(false);
  const [selectedFilters, setSelectedFilters] = React.useState<DataSourceFilterEnum[]>(selected);
  const viewableDataSources = useRecoilValue(allDataSourcesSelector);
  const viewableDataSourcesEnums = useMemo(
    () => viewableDataSources.map((source) => DataSourceFilterEnum.Source({ source })),
    [viewableDataSources],
  );
  useEffect(() => {
    setSelectedFilters(
      selected.some(DataSourceFilterEnum.$is('AllIntegrations'))
        ? [...selected, ...viewableDataSourcesEnums]
        : selected,
    );
  }, [selected, viewableDataSourcesEnums]);
  const isChecked = (dataSource: DataSourceFilterEnum) =>
    selectedFilters.some((selected) => Equal.equals(selected, dataSource)) ? 'checked' : 'unchecked';
  const isAllIntegrationsSelected = selectedFilters.some(DataSourceFilterEnum.$is('AllIntegrations'));
  const integrationsCheckboxState = Match.value([
    isAllIntegrationsSelected,
    selectedFilters.filter(DataSourceFilterEnum.$is('Source')).length,
  ]).pipe(
    Match.when([true, Match.any], () => 'checked' as const),
    Match.when([Match.any, 0], () => 'unchecked' as const),
    Match.when([Match.any, viewableDataSourcesEnums.length], () => 'checked' as const),
    Match.orElse(() => 'indeterminate' as const),
  );
  const allDataSources = [
    ...viewableDataSourcesEnums,
    DataSourceFilterEnum.UploadedByMe(),
    DataSourceFilterEnum.UploadedByTeam(),
    DataSourceFilterEnum.VennProvided(),
    DataSourceFilterEnum.AllIntegrations(),
  ];
  const isAllItemsSelected = selectedFilters.length === allDataSources.length;

  return {
    state: {
      open,
      isChecked,
      integrationsCheckboxState,
      isAllItemsSelected,
      viewableDataSourcesEnums,
      selectedFilters,
      originallySelectedFilters: selected,
    },
    mutations: {
      setOpen,
      setSelectedFilters,
    },
  };
};
